import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";

const LANGS = ["en", "ru", "es"];

function I18n({ label, value, onChange, multiline }) {
  const v = value || { en: "", ru: "", es: "" };
  return (
    <div className="border border-white/10 p-3 bg-white/[0.02]">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">{label}</div>
      <div className="space-y-2">
        {LANGS.map((L) => (
          <div key={L} className="flex items-start gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 w-6 mt-2">{L}</span>
            {multiline ? (
              <textarea
                data-testid={`site-i18n-${L}-${label}`}
                value={v[L] || ""}
                rows={2}
                onChange={(e) => onChange({ ...v, [L]: e.target.value })}
                className="flex-1 px-3 py-2 bg-transparent border border-white/20 text-white font-sans text-sm focus:outline-none focus:border-white/60 resize-y"
              />
            ) : (
              <input
                data-testid={`site-i18n-${L}-${label}`}
                value={v[L] || ""}
                onChange={(e) => onChange({ ...v, [L]: e.target.value })}
                className="flex-1 px-3 py-2 bg-transparent border border-white/20 text-white font-sans text-sm focus:outline-none focus:border-white/60"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1.5">{label}</div>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-transparent border border-white/30 text-white font-sans text-sm focus:outline-none focus:border-[#00d4ff]"
      />
    </label>
  );
}

function SaveBar({ busy, ok, err, onSave, testid }) {
  return (
    <div className="flex items-center justify-between pt-3 border-t border-dashed border-white/10">
      <div>
        {ok && <span className="font-mono text-xs text-[#00d4ff]">✓ Saved</span>}
        {err && <span className="font-mono text-xs text-[#ff006e]">{err}</span>}
      </div>
      <button
        onClick={onSave}
        disabled={busy}
        data-testid={testid}
        className="inline-flex items-center gap-2 px-5 py-2 bg-[#00d4ff] text-[#0a0a0a] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white transition-colors disabled:opacity-50"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
        Save
      </button>
    </div>
  );
}

/* --------------------- HERO ---------------------- */
export function HeroEditor() {
  const [data, setData] = useState({ eyebrow: {}, title: {}, sub: {}, meta: {}, slides: [] });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/site/hero").then((r) => setData({ eyebrow: {}, title: {}, sub: {}, meta: {}, slides: [], ...(r.data?.data || {}) }));
  }, []);

  const save = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      await api.put("/admin/site/hero", { data });
      setOk(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <I18n label="Eyebrow" value={data.eyebrow} onChange={(v) => setData({ ...data, eyebrow: v })} />
      <I18n label="Title (word 2 becomes pink italic)" value={data.title} onChange={(v) => setData({ ...data, title: v })} />
      <I18n label="Subtitle" value={data.sub} onChange={(v) => setData({ ...data, sub: v })} />
      <I18n label="Meta line" value={data.meta} onChange={(v) => setData({ ...data, meta: v })} />

      <div className="border border-white/10 p-3 bg-white/[0.02]">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">Slideshow images ({data.slides.length})</div>
          <button
            onClick={() => setData({ ...data, slides: [...(data.slides || []), { src: "", tag: "" }] })}
            data-testid="site-slide-add"
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#00d4ff] hover:text-[#0a0a0a]"
          ><Plus size={11}/> Add</button>
        </div>
        <div className="space-y-2">
          {(data.slides || []).map((s, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                data-testid={`site-slide-src-${i}`}
                placeholder="Image URL"
                value={s.src || ""}
                onChange={(e) => {
                  const next = [...data.slides]; next[i] = { ...next[i], src: e.target.value }; setData({ ...data, slides: next });
                }}
                className="flex-1 px-3 py-2 bg-transparent border border-white/20 text-white font-sans text-sm"
              />
              <input
                data-testid={`site-slide-tag-${i}`}
                placeholder="tag"
                value={s.tag || ""}
                onChange={(e) => {
                  const next = [...data.slides]; next[i] = { ...next[i], tag: e.target.value }; setData({ ...data, slides: next });
                }}
                className="w-40 px-3 py-2 bg-transparent border border-white/20 text-white font-sans text-sm"
              />
              <button
                onClick={() => setData({ ...data, slides: data.slides.filter((_, j) => j !== i) })}
                className="p-2 border border-white/20 hover:border-[#ff006e] hover:text-[#ff006e]"
                data-testid={`site-slide-del-${i}`}
              ><Trash2 size={12}/></button>
            </div>
          ))}
        </div>
      </div>

      <SaveBar busy={busy} ok={ok} err={err} onSave={save} testid="site-hero-save" />
    </div>
  );
}

/* --------------------- ABOUT ---------------------- */
export function AboutEditor() {
  const [data, setData] = useState({ title: {}, role: {}, bio: {}, quote: {}, stats: [], image_a: "", image_b: "" });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/site/about").then((r) => setData({ title: {}, role: {}, bio: {}, quote: {}, stats: [], image_a: "", image_b: "", ...(r.data?.data || {}) }));
  }, []);

  const save = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      await api.put("/admin/site/about", { data });
      setOk(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <I18n label="Title" value={data.title} onChange={(v) => setData({ ...data, title: v })} />
      <I18n label="Role" value={data.role} onChange={(v) => setData({ ...data, role: v })} />
      <I18n label="Bio" value={data.bio} onChange={(v) => setData({ ...data, bio: v })} multiline />
      <I18n label="Quote" value={data.quote} onChange={(v) => setData({ ...data, quote: v })} multiline />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Image A URL (top-left tilted)" value={data.image_a} onChange={(v) => setData({ ...data, image_a: v })} />
        <TextField label="Image B URL (main)" value={data.image_b} onChange={(v) => setData({ ...data, image_b: v })} />
      </div>

      <div className="border border-white/10 p-3 bg-white/[0.02]">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">Stats</div>
          <button
            onClick={() => setData({ ...data, stats: [...(data.stats || []), { value: "", label: { en: "", ru: "", es: "" } }] })}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#00d4ff] hover:text-[#0a0a0a]"
          ><Plus size={11}/> Add</button>
        </div>
        {(data.stats || []).map((s, i) => (
          <div key={i} className="flex gap-2 items-start mb-2">
            <input
              placeholder="Value (e.g. 50+)"
              value={s.value || ""}
              onChange={(e) => {
                const next = [...data.stats]; next[i] = { ...next[i], value: e.target.value }; setData({ ...data, stats: next });
              }}
              className="w-28 px-3 py-2 bg-transparent border border-white/20 text-white font-sans text-sm"
            />
            <div className="flex-1">
              <I18n label={`Label ${i + 1}`} value={s.label} onChange={(v) => {
                const next = [...data.stats]; next[i] = { ...next[i], label: v }; setData({ ...data, stats: next });
              }} />
            </div>
            <button
              onClick={() => setData({ ...data, stats: data.stats.filter((_, j) => j !== i) })}
              className="p-2 border border-white/20 hover:border-[#ff006e] hover:text-[#ff006e]"
            ><Trash2 size={12}/></button>
          </div>
        ))}
      </div>

      <SaveBar busy={busy} ok={ok} err={err} onSave={save} testid="site-about-save" />
    </div>
  );
}

/* ---------------------- COLLABORATORS ---------------------- */
export function CollaboratorsEditor() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/collaborators").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = () => setEditing({
    name: "", handle: "", role: "", tags: [], tools: "",
    bio: { en: "", ru: "", es: "" }, image: "", accent: "#00d4ff",
    order: items.length, status: "published", _new: true,
  });

  const remove = async (id) => {
    if (!window.confirm("Delete this collaborator?")) return;
    await api.delete(`/admin/collaborators/${id}`);
    load();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{items.length} collaborators</div>
        <button
          onClick={add}
          data-testid="collab-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#00d4ff] text-[#0a0a0a] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white"
        ><Plus size={12}/> Add collaborator</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((c, i) => (
          <div key={c.collab_id} data-testid={`collab-card-${i}`} className="border border-white/20 bg-white/[0.02] p-4 flex gap-4">
            {c.image && <img src={c.image} alt="" className="w-20 h-24 object-cover border border-white/20" />}
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm text-white truncate">{c.name}</div>
              <div className="font-mono text-[10px] text-white/50 truncate">{c.handle} · {c.role}</div>
              <div className="mt-1 font-mono text-[9px] text-white/40">{c.status}</div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setEditing(c)} data-testid={`collab-edit-${i}`} className="px-3 py-1.5 border border-white/30 hover:border-[#00d4ff] font-mono text-[10px] uppercase">Edit</button>
                <button onClick={() => remove(c.collab_id)} data-testid={`collab-delete-${i}`} className="px-3 py-1.5 border border-white/30 hover:border-[#ff006e] hover:text-[#ff006e] font-mono text-[10px]"><Trash2 size={11}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <CollabEditor item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CollabEditor({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...item,
    tags: Array.isArray(item.tags) ? item.tags : [],
    bio: typeof item.bio === "object" ? item.bio : { en: item.bio || "", ru: "", es: "" },
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      delete payload._new; delete payload.collab_id;
      delete payload.created_at; delete payload.updated_at;
      if (item._new) await api.post("/admin/collaborators", payload);
      else await api.put(`/admin/collaborators/${item.collab_id}`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-md overflow-y-auto" onClick={onClose} data-testid="collab-editor-modal">
      <div className="max-w-2xl mx-auto my-8 bg-[#111] border border-white/20 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-white/20 flex items-center justify-between">
          <h3 className="font-mono text-sm uppercase tracking-[0.25em]">Collaborator</h3>
          <button onClick={onClose} className="hover:text-[#ff006e]">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <TextField label="Handle (e.g. @user)" value={form.handle} onChange={(v) => setForm({ ...form, handle: v })} />
            <TextField label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
            <TextField label="Tools (text)" value={form.tools} onChange={(v) => setForm({ ...form, tools: v })} />
            <TextField label="Image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
            <TextField label="Accent color" value={form.accent} onChange={(v) => setForm({ ...form, accent: v })} />
            <TextField label="Tags (comma separated)" value={form.tags.join(", ")} onChange={(v) => setForm({ ...form, tags: v.split(",").map((x) => x.trim()).filter(Boolean) })} />
            <TextField label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v) || 0 })} />
          </div>
          <I18n label="Bio" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} multiline />
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Status</span>
            <button
              onClick={() => setForm({ ...form, status: form.status === "published" ? "draft" : "published" })}
              className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.2em] ${
                form.status === "published" ? "border-[#00d4ff] text-[#00d4ff]" : "border-white/40 text-white/70"
              }`}
            >{form.status}</button>
          </div>
          {err && <div className="font-mono text-xs text-[#ff006e]">{err}</div>}
          <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
            <button onClick={onClose} className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-white px-3 py-2">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="collab-save" className="inline-flex items-center gap-2 px-5 py-2 bg-[#00d4ff] text-[#0a0a0a] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
